import {
	SET_OP_CODE_MISMATCH_RESULTS,
	SET_OP_CODE_MISMATCH_ORDER,
	SET_OPERATION_MISMATCH_RESULTS,
	REMOVE_OPERATION_MISMATCH_RESULTS,
	SET_LABOR_RATE_INFO,
	SET_OP_CODE_SELECTED,
	SET_ALL_OP_CODES_SELECTED
} from './labor-rates-mutation-types';
import api from '@/services/api-service';
import buildRoute from '@/composables/build-route';
import { cloneDeep } from 'lodash';
import Vue from 'vue';
import gatherItems from '@/utils/gather-items';

const initialState = {
	opCodeMismatchResults: {},
	operationMismatchResults: {},
	laborRateId: null,
	laborRateCode: null,
	selectedOpCodes: [],
	pageSize: 30
};
export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'laborRates',
	namespaced: true,
	state: cloneDeep(initialState),
	mutations: {
		[SET_OP_CODE_MISMATCH_RESULTS](state, response) {
			const items =
                response.items?.map(item => ({
                    ...item,
                    disabled: item.items.every(
                        i =>
                            i.fixedPrice > 0 ||
                            Math.round(i.laborAmount * 100) / 100 !==
                            Math.round((Number(i.laborHours) || 0) * (Number(i.laborRate) || 0) * 100) / 100
                    )
                })) || [];
			Vue.set(state.opCodeMismatchResults, 'items', items);
		},
		[SET_OP_CODE_MISMATCH_ORDER](state, items) {
			Vue.set(state.opCodeMismatchResults, 'items', [...items]);
		},
		[SET_OPERATION_MISMATCH_RESULTS](state, response) {
			Vue.set(
				state.operationMismatchResults,
				response.page + '',
				response
			);
		},
		[REMOVE_OPERATION_MISMATCH_RESULTS](state) {
			const firstPage = state.operationMismatchResults['1'] || {};
			Vue.set(state, 'operationMismatchResults', { '1': firstPage });
		},
		[SET_LABOR_RATE_INFO](state, laborRate) {
			state.laborRateId = laborRate.id;
			state.laborRateCode = laborRate.rateCode;
		},
		[SET_OP_CODE_SELECTED](state, { opCodeId, selected }) {
			let index = state.selectedOpCodes.findIndex(id => id === opCodeId);

			if (selected && index === -1) {
				state.selectedOpCodes.push(opCodeId);
				index = 0;
			} else if (!selected && index !== -1) {
				state.selectedOpCodes.splice(index, 1);
			}

			const opCode = state.opCodeMismatchResults.items?.find(
				oc => oc.id === opCodeId && !oc.disabled
			);

			if (opCode) {
				Vue.set(opCode, 'selected', selected);
			}
		},
		[SET_ALL_OP_CODES_SELECTED](state, { selected }) {
			state.opCodeMismatchResults.items?.forEach(opCode => {
				if (!opCode.disabled) {
					Vue.set(opCode, 'selected', selected);
				}
			});

			if (selected) {
				state.selectedOpCodes = state.opCodeMismatchResults.items
					.filter(opCode => opCode.selected)
					.map(opCode => opCode.id);
			} else {
				state.selectedOpCodes = [];
			}
		}
	},
	actions: {
		// actions let us get to ({ state, getters, commit, dispatch }) {
		async getOpCodeMismatchesAction(
			{ commit },
			{ companyId, laborRateId }
		) {
			const route = buildRoute(
				'/service/company/{companyId}/laborrate/{laborRateId}/opcode-mismatches',
				{
					companyId,
					laborRateId
				}
			);
			const response = await api.get(route);

			commit(SET_OP_CODE_MISMATCH_RESULTS, response);
			commit(SET_LABOR_RATE_INFO, response);
		},
		async orderOpCodeMismatchesAction(
			{ commit, state },
			{ sortDirection }
		) {
			let opCodes = [...state.opCodeMismatchResults?.items];
			if (opCodes.length > 0) {
				opCodes = opCodes.sort((a, b) => {
					if (sortDirection === 'asc') {
						return a.opCode.localeCompare(b.opCode);
					} else {
						return b.opCode.localeCompare(a.opCode);
					}
				});
			}
			commit(SET_OP_CODE_MISMATCH_ORDER, opCodes);
		},
		resetOperationMismatchesAction({ commit }) {
			commit(REMOVE_OPERATION_MISMATCH_RESULTS);
		},
		async getOperationMismatchesAction(
			{ commit, state },
			{ companyId, laborRateId, page, sortBy }
		) {
			const pageSize = state.pageSize;
			const route = buildRoute(
				'/service/company/{companyId}/laborrate/{laborRateId}/operation-mismatches?page={page}&maxCount={pageSize}&sortby={sortby}',
				{
					companyId,
					laborRateId,
					page,
					pageSize,
					sortBy
				}
			);
			const response = await api.get(route);
			commit(SET_OPERATION_MISMATCH_RESULTS, response);
			commit(SET_LABOR_RATE_INFO, response);
		},
		async updateOpCodeMismatchesAction(
			{ commit, state },
			{ companyId, laborRateId, errorHandler }
		) {
			const route = buildRoute(
				'/service/company/{companyId}/laborrate/{laborRateId}/opcode-mismatches',
				{
					companyId,
					laborRateId
				}
			);

			const response = await api.post(
				route,
				{
					laborRateId: laborRateId,
					opCodeIds: state.selectedOpCodes
				},
				errorHandler
			);

			commit(SET_OP_CODE_MISMATCH_RESULTS, response);
			commit(SET_LABOR_RATE_INFO, response);
			commit(SET_ALL_OP_CODES_SELECTED, { selected: false });
		},
		selectOpCodeAction({ commit }, { opCodeId, selected }) {
			commit(SET_OP_CODE_SELECTED, { opCodeId, selected });
		},
		selectAllOpCodesAction({ commit }, { selected }) {
			commit(SET_ALL_OP_CODES_SELECTED, { selected });
		}
	},
	getters: {
		opCodeMismatchResults: state => state.opCodeMismatchResults,
		operationMismatchItems: state => {
			return gatherItems(state.operationMismatchResults);
		},
		laborRateId: state => state.laborRateId,
		laborRateCode: state => state.laborRateCode,
		selectedOpCodes: state => state.selectedOpCodes,
		selectedOpCodesCount: state => state.selectedOpCodes.length,
		opCodeMismatchCount: state =>
			state.opCodeMismatchResults?.items?.length || 0,
		operationMismatchesPageSize: state => state.pageSize,
		operationMismatchesCount: state => {
			let count = 0;

			if (
				state.operationMismatchResults &&
				state.operationMismatchResults[1]
			) {
				count = state.operationMismatchResults[1].totalCount;
			}

			return count;
		},
		operationMismatchesPageCount: state => {
			let count = 1;

			if (
				state.operationMismatchResults &&
				state.operationMismatchResults[1]
			) {
				count = state.operationMismatchResults[1].totalPageCount;
			}

			return count;
		}
	}
};
